<i18n
  src="@/locales/views/service_provider/delivery/reporting/show_process.yml"
  lang="yaml"
></i18n>

<i18n src="@/locales/models/order.yml" lang="yaml"></i18n>

<template>
  <go-back-link
    :text="$t('labels.go_back_link')"
    :name="routeName('list_processes')"
    :params="{ service_provider_id: service_provider_id }"
  />

  <bread-crumbs :labels="[module_name, delivery_process.name]" />

  <div class=" flex flex-row justify-end gap-x-2">
    <button
      type="button"
      @click.prevent="downloadWeeklyXlsx()"
      class="btn-white hidden md:inline"
    >
      <i
        :class="['fa-solid', downloading_weekly ? 'fa-sync fa-spin' : 'fa-file-excel']"
      >
      </i>
      <span class="space-icon"></span>
      {{ $t("labels.export_weekly_button") }}
    </button>
    <button
      type="button"
      @click.prevent="downloadXlsx()"
      class="btn-white hidden md:inline"
    >
      <i
        :class="['fa-solid', downloading ? 'fa-sync fa-spin' : 'fa-file-excel']"
      >
      </i>
      <span class="space-icon"></span>
      {{ $t("labels.export_button") }}
    </button>
  </div>

  <div class="mt-8">
    <div class="panel">
      <div class="panel-body">
        <template v-if="load_general_data">
          <div
            v-if="orders.length === 0"
            class="text-center py-8 text-gray-500"
          >
            {{ $t("labels.no_data") }}
          </div>

          <div v-else class="space-y-8">
            <!-- Agrupar por fecha -->
            <div
              v-for="(group, dateKey) in ordersByDate"
              :key="dateKey"
              class="space-y-4"
            >
              <!-- Date Header -->
              <div
                class="sticky top-0 z-10 bg-gray-200 border-l-4 border-green-600 px-4 py-3"
              >
                <h3 class="text-lg font-bold text-gray-900">
                  {{ getDateLabel(dateKey) }}
                  <p class="flex text-base text-gray-600 mt-1 gap-x-4">
                    <span>
                      {{ group.summary.total_orders }}
                      {{ $t("labels.orders") }}:
                      {{ group.summary.quantity_orders }}
                    </span>
                    |
                    <span>
                      {{ group.summary.total_deliveries }}
                      {{ $t("labels.deliveries_count") }}:
                      {{ group.summary.quantity_deliveries }}
                    </span>
                    |
                    <span>
                      {{ group.summary.total_collections }}
                      {{ $t("labels.collections_count") }}:
                      {{ group.summary.quantity_collections }}
                    </span>
                  </p>
                </h3>
              </div>

              <!-- Orders Table -->
              <div class="overflow-x-auto">
                <table class="min-w-full divide-y divide-gray-200">
                  <thead class="bg-gray-50">
                    <tr>
                      <th
                        class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase"
                      >
                        {{ $t("labels.order_code") }}
                      </th>
                      <th
                        class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase"
                      >
                        {{ $t("labels.customer") }}
                      </th>
                      <th
                        class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase"
                      >
                        {{ $t("labels.products") }}
                      </th>
                      <th
                        class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase"
                      >
                        {{ $t("labels.quantity") }}
                      </th>
                      <th
                        class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase"
                      >
                        {{ $t("labels.deliveries_count") }}
                      </th>
                      <th
                        class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase"
                      >
                        {{ $t("labels.collections_count") }}
                      </th>
                      <th
                        class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase"
                      >
                        {{ $t("labels.status") }}
                      </th>
                      <th
                        class="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase"
                      >
                        {{ $t("labels.total") }}
                      </th>
                    </tr>
                  </thead>
                  <tbody class="bg-white divide-y divide-gray-200">
                    <template
                      v-for="(order, orderIndex) in group.orders"
                      :key="orderIndex"
                    >
                      <tr
                        class="hover:bg-gray-50 cursor-pointer"
                        @click.prevent="
                          order.show_details = !order.show_details
                        "
                      >
                        <td
                          class="px-3 py-3 whitespace-nowrap text-sm font-semibold text-gray-900"
                        >
                          {{ order.order_code }}
                        </td>
                        <td
                          class="px-3 py-3 whitespace-nowrap text-sm text-gray-900"
                        >
                          {{ order.delivery_process_customer.name }}
                        </td>
                        <td class="px-3 py-3 text-sm text-gray-700">
                          <div class="flex flex-wrap gap-1">
                            <span
                              v-for="(product, idx) in getOrderProducts(order)"
                              :key="idx"
                              class="inline-flex items-center px-2 py-0.5 rounded text-sm"
                            >
                              {{ product }}
                            </span>
                          </div>
                        </td>
                        <td
                          class="px-3 py-3 whitespace-nowrap text-sm text-gray-900"
                        >
                          {{ getOrderTotalQuantity(order) }}
                        </td>
                        <td
                          class="px-3 py-3 whitespace-nowrap text-sm text-gray-900"
                        >
                          {{ getOrderDeliveriesQuantity(order) }}
                          <span
                            class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"
                          >
                            {{ getOrderDeliveriesCount(order) }}
                          </span>
                        </td>
                        <td
                          class="px-3 py-3 whitespace-nowrap text-sm text-gray-900"
                        >
                          {{ getOrderCollectionsQuantity(order) }}
                          <span
                            class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                          >
                            {{ getOrderCollectionsCount(order) }}
                          </span>
                        </td>
                        <td class="px-3 py-3 whitespace-nowrap">
                          <BadgeDefault
                            :label="
                              $t(`model.order.statuses.${order.status}.label`)
                            "
                            :tw-bg="
                              $t(`model.order.statuses.${order.status}.bg`)
                            "
                            :tw-text="
                              $t(`model.order.statuses.${order.status}.text`)
                            "
                          />
                        </td>
                        <td
                          class="px-3 py-3 whitespace-nowrap text-sm font-semibold text-gray-900 text-right"
                        >
                          S/ {{ formatPrice(getOrderTotal(order)) }}
                        </td>
                      </tr>

                      <!-- Order Details (Expandable) -->
                      <tr
                        v-if="order.show_details"
                        :key="`${order?.id}-details`"
                      >
                        <td colspan="8" class="px-3 py-4 bg-gray-50">
                          <div class="space-y-4">
                            <!-- Deliveries Summary -->
                            <div v-if="getOrderDeliveriesCount(order) > 0">
                              <h5
                                class="text-xs font-semibold text-gray-700 uppercase mb-2"
                              >
                                {{ $t("labels.deliveries") }}
                              </h5>
                              <div
                                class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3"
                              >
                                <div
                                  v-for="(delivery, dIdx) in getOrderDeliveries(
                                    order
                                  )"
                                  :key="dIdx"
                                  class="bg-white border border-green-300 rounded-lg p-3"
                                >
                                  <div
                                    class="flex justify-between items-start mb-2"
                                  >
                                    <span
                                      class="text-xs font-semibold text-gray-900"
                                    >
                                      {{ delivery.delivery_code }}
                                    </span>
                                    <span
                                      v-if="delivery.delivered"
                                      class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800"
                                    >
                                      Entregado
                                    </span>
                                  </div>
                                  <div class="text-xs text-gray-600 space-y-1">
                                    <div>
                                      <span class="font-medium"
                                        >{{ $t("labels.quantity") }}:</span
                                      >
                                      {{ delivery.quantity }}
                                    </div>
                                    <div>
                                      <span class="font-medium"
                                        >{{
                                          $t("labels.delivery_employee")
                                        }}:</span
                                      >
                                      {{ delivery.employee.name }}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <!-- Collections Summary -->
                            <div v-if="getOrderCollectionsCount(order) > 0">
                              <h5
                                class="text-xs font-semibold text-gray-700 uppercase mb-2"
                              >
                                {{ $t("labels.collections") }}
                              </h5>
                              <div
                                class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3"
                              >
                                <div
                                  v-for="(
                                    collection, cIdx
                                  ) in getOrderCollections(order)"
                                  :key="cIdx"
                                  class="bg-white border border-green-300 rounded-lg p-3"
                                >
                                  <div
                                    class="text-xs font-semibold text-gray-900 mb-2"
                                  >
                                    {{ collection.collection_code }}
                                  </div>
                                  <div class="text-xs text-gray-600 space-y-1">
                                    <div>
                                      <span class="font-medium"
                                        >{{ $t("labels.provider") }}:</span
                                      >
                                      {{ collection.provider }}
                                    </div>
                                    <div>
                                      <span class="font-medium"
                                        >{{ $t("labels.quantity") }}:</span
                                      >
                                      {{ collection.quantity }}
                                    </div>
                                    <div>
                                      <span class="font-medium"
                                        >{{
                                          $t("labels.collection_employee")
                                        }}:</span
                                      >
                                      {{ collection.employee }}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>

              <!-- Date Summary Footer -->
              <div class="bg-gray-50 px-4 py-3 border-t border-gray-200">
                <div class="flex justify-between items-center text-sm">
                  <span class="font-medium text-gray-700">
                    {{ $t("labels.total_date") }}
                  </span>

                  <span class="text-lg font-bold text-gray-900">
                    S/ {{ formatPrice(group.summary.total_amount) }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </template>

        <loading-message v-else />
      </div>
    </div>
  </div>
</template>

<script>
import BadgeDefault from "@/components/views/layout/BadgeDefault.vue";
import Requester from "@/views/service_provider/requesters/delivery/reporting/show_process.js";
import moment from "moment";

export default {
  name: "SPDeliveryReportingShowProcess",
  components: { BadgeDefault },
  data() {
    return {
      load_general_data: false,
      delivery_process: {},
      orders: [],
      downloading_weekly: false,
      downloading: false
    };
  },
  props: {
    service_provider_id: { required: true },
    delivery_process_id: { required: true }
  },
  computed: {
    ordersByDate() {
      const grouped = {};

      this.orders.forEach(order => {
        const orderDate = moment(order.ordered_at).format("YYYY-MM-DD");

        if (!grouped[orderDate]) {
          grouped[orderDate] = {
            orders: [],
            summary: {
              total_orders: 0,
              quantity_orders: 0,
              total_deliveries: 0,
              quantity_deliveries: 0,
              total_collections: 0,
              quantity_collections: 0,
              total_amount: 0
            }
          };
        }

        grouped[orderDate].orders.push(order);
        grouped[orderDate].summary.total_orders++;
        grouped[orderDate].summary.quantity_orders +=
          this.getOrderTotalQuantity(order);
        grouped[orderDate].summary.total_deliveries +=
          this.getOrderDeliveriesCount(order);
        grouped[orderDate].summary.quantity_deliveries +=
          this.getOrderDeliveriesQuantity(order);
        grouped[orderDate].summary.total_collections +=
          this.getOrderCollectionsCount(order);
        grouped[orderDate].summary.quantity_collections +=
          this.getOrderCollectionsQuantity(order);
        grouped[orderDate].summary.total_amount += this.getOrderTotal(order);
      });

      // Sort by date descending (most recent first)
      const sortedKeys = Object.keys(grouped).sort((a, b) =>
        moment(b).diff(moment(a))
      );
      const sortedGrouped = {};
      sortedKeys.forEach(key => {
        sortedGrouped[key] = grouped[key];
      });

      return sortedGrouped;
    }
  },
  created() {
    this.checkIfAuthenticated().then(() =>
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          service_provider_id: this.service_provider_id
        }
      }).then(() => this.loadData())
    );
  },
  methods: {
    loadData: async function () {
      await this.fetchShowProcess();
      this.load_general_data = true;
    },

    fetchShowProcess: async function () {
      try {
        const response = await Requester.fetchShowProcess(
          this.service_provider_id,
          this.delivery_process_id
        );
        this.delivery_process = response.data;
        this.orders = response.data.orders || [];
      } catch (error) {
        console.error("Error fetching orders:", error);
        this.orders = [];
      }
    },

    getDateLabel: function (dateKey) {
      const today = moment().format("YYYY-MM-DD");
      const yesterday = moment().subtract(1, "days").format("YYYY-MM-DD");

      if (dateKey === today) {
        return this.$t("labels.today");
      } else if (dateKey === yesterday) {
        return this.$t("labels.yesterday");
      } else {
        return moment(dateKey).format("dddd, DD [de] MMMM [de] YYYY");
      }
    },

    getOrderProducts: function (order) {
      const products = new Set();
      if (order.order_items) {
        order.order_items.forEach(item => {
          if (item.delivery_product) {
            products.add(
              `${item.delivery_product.crop.name}(${item.delivery_product.unit_of_measure})`
            );
          }
        });
      }
      return Array.from(products);
    },

    getOrderTotalQuantity: function (order) {
      let total = 0;
      if (order.order_items) {
        order.order_items.forEach(item => {
          total += parseFloat(item.quantity || 0);
        });
      }
      return total;
    },

    getOrderDeliveriesQuantity: function (order) {
      let total = 0;
      if (order.order_items) {
        order.order_items.forEach(item => {
          if (item.delivery_items) {
            item.delivery_items.forEach(deliveryItem => {
              total += deliveryItem.quantity;
            });
          }
        });
      }
      return total;
    },

    getOrderCollectionsQuantity: function (order) {
      let total = 0;
      if (order.order_items) {
        order.order_items.forEach(item => {
          if (item.delivery_items) {
            item.delivery_items.forEach(deliveryItem => {
              const collectionItem = deliveryItem.delivery_collection_item;
              if (collectionItem) {
                total += collectionItem.quantity;
              }
            });
          }
        });
      }
      return total;
    },

    getOrderTotal: function (order) {
      let total = 0;
      if (order.order_items) {
        order.order_items.forEach(item => {
          total += parseFloat(item.total_price || 0);
        });
      }
      return total;
    },

    getOrderDeliveriesCount: function (order) {
      let count = 0;
      if (order.order_items) {
        order.order_items.forEach(item => {
          if (item.delivery_items) {
            count += item.delivery_items.length;
          }
        });
      }
      return count;
    },

    getOrderCollectionsCount: function (order) {
      let count = 0;
      if (order.order_items) {
        order.order_items.forEach(item => {
          if (item.delivery_items) {
            item.delivery_items.forEach(deliveryItem => {
              if (deliveryItem.delivery_collection_item) {
                count++;
              }
            });
          }
        });
      }
      return count;
    },

    getOrderDeliveries: function (order) {
      const deliveries = [];
      if (order.order_items) {
        order.order_items.forEach(item => {
          if (item.delivery_items) {
            item.delivery_items.forEach(deliveryItem => {
              const hasCollection = !!deliveryItem.delivery_collection_item;
              deliveries.push({
                delivery_code: deliveryItem.delivery?.delivery_code,
                quantity: deliveryItem.quantity,
                delivered: !!deliveryItem.delivery?.delivered_at,
                employee: deliveryItem.delivery?.user,
                has_collection: hasCollection
              });
            });
          }
        });
      }
      return deliveries;
    },

    getOrderCollections: function (order) {
      const collections = [];
      if (order.order_items) {
        order.order_items.forEach(item => {
          if (item.delivery_items) {
            item.delivery_items.forEach(deliveryItem => {
              const collectionItem = deliveryItem.delivery_collection_item;
              if (collectionItem) {
                const collection = collectionItem.delivery_collection;
                collections.push({
                  collection_code: collection?.collection_code,
                  quantity: collectionItem.quantity,
                  provider: collectionItem.delivery_process_provider.name,
                  employee: collection.user.name
                });
              }
            });
          }
        });
      }
      return collections;
    },

    getStatusClass: function (status) {
      const statusClasses = {
        pending: "bg-yellow-100 text-yellow-800",
        confirmed: "bg-blue-100 text-blue-800",
        delivered: "bg-green-100 text-green-800",
        cancelled: "bg-red-100 text-red-800",
        in_progress: "bg-indigo-100 text-indigo-800",
        completed: "bg-green-100 text-green-800"
      };
      return statusClasses[status] || "bg-gray-100 text-gray-800";
    },

    formatPrice: function (price) {
      return parseFloat(price || 0).toFixed(2);
    },

    downloadXlsx: async function () {
      this.downloading = true;
      const response = await Requester.downloadXlsx(
        this.service_provider_id,
        this.delivery_process_id
      );

      let file = response.data;
      this.downloadFile(
        file,
        `${this.delivery_process.name.split(" ").join("-")}-${moment().format(
          "YYYY-MM-DD"
        )}.xlsx`
      );
      this.downloading = false;
    },

    downloadWeeklyXlsx: async function () {
      this.downloading_weekly = true;
      const response = await Requester.downloadWeeklyXlsx(
        this.service_provider_id,
        this.delivery_process_id
      );

      let file = response.data;
      this.downloadFile(
        file,
        `${this.delivery_process.name.split(" ").join("-")}-Sem${moment().week()}-${moment().format(
          "YYYY-MM-DD"
        )}.xlsx`
      );
      this.downloading_weekly = false;
    }
  }
};
</script>
